import { usePartnerInstances } from '@/api/hooks/useTenantConfig'
import type { Scope, User } from '@/api/types'
import { Form, Input, Modal, Select, Switch } from 'antd'
import React, { useEffect, useMemo } from 'react'
import type { TenantOption } from '../types'

/**
 * Record (not array) so adding a Scope in the backend enum without adding it
 * here is a TS error, keeping this dropdown in sync with app/scopes.py.
 */
const SCOPE_LABELS = {
  manage_users: 'manage_users',
  manage_tenants: 'manage_tenants',
  manage_roles: 'manage_roles',
  'crossroads_logs.view': 'crossroads_logs.view',
  'crossroads_systems.view': 'crossroads_systems.view',
  'crossroads_systems.edit': 'crossroads_systems.edit',
  'crossroads_entities.view': 'crossroads_entities.view',
  'crossroads_entities.edit': 'crossroads_entities.edit',
  'crossroads_mappings.view': 'crossroads_mappings.view',
  'crossroads_mappings.edit': 'crossroads_mappings.edit',
  'crossroads.retry': 'crossroads.retry',
  order_management: 'order_management',
  integration: 'integration',
  tenant_configuration: 'tenant_configuration',
} as const satisfies Record<Scope, string>

const SCOPE_OPTIONS = Object.keys(SCOPE_LABELS) as Scope[]

interface CreateApiKeyModalProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
  availableTenants: TenantOption[]
  onSubmit: (values: {
    email: string
    client_id: string
    client_secret: string
    for_domain: string
    instance_id: string
    scopes: string[]
    active: boolean
  }) => void
  isLoading: boolean
}

export const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  isOpen,
  onClose,
  user,
  availableTenants,
  onSubmit,
  isLoading,
}) => {
  const [form] = Form.useForm()
  const selectedDomain = Form.useWatch('for_domain', form) as string | undefined

  const selectedTenantId = useMemo(
    () => availableTenants.find((t) => t.name === selectedDomain)?.id,
    [availableTenants, selectedDomain],
  )
  const { data: domainInstances, isLoading: isLoadingInstances } =
    usePartnerInstances(selectedTenantId)

  useEffect(() => {
    if (isOpen && user) {
      form.setFieldsValue({
        email: user.email,
        client_id: undefined,
        client_secret: undefined,
        for_domain: undefined,
        instance_id: undefined,
        scopes: [],
        active: true,
      })
    }
  }, [isOpen, user, form])

  const handleOk = async () => {
    const values = await form.validateFields()
    onSubmit(values)
  }

  return (
    <Modal
      title='Create API Key'
      open={isOpen}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={isLoading}
      destroyOnClose
    >
      <Form
        form={form}
        layout='vertical'
        initialValues={{ active: true, scopes: [] }}
      >
        <Form.Item label='Email' name='email' rules={[{ required: true }]}>
          <Input disabled />
        </Form.Item>
        <Form.Item
          label='Client ID'
          name='client_id'
          rules={[{ required: true, message: 'Please enter a client ID' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label='Client Secret'
          name='client_secret'
          rules={[{ required: true, message: 'Please enter a client secret' }]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          label='Tenant'
          name='for_domain'
          rules={[{ required: true, message: 'Please select a tenant' }]}
        >
          <Select
            placeholder='Select a tenant'
            showSearch
            optionFilterProp='label'
            options={availableTenants.map((t) => ({
              label: t.name,
              value: t.name,
            }))}
            onChange={() => form.setFieldValue('instance_id', undefined)}
          />
        </Form.Item>
        <Form.Item
          label='Instance'
          name='instance_id'
          rules={[{ required: true, message: 'Please select an instance' }]}
        >
          <Select
            placeholder='Select an instance'
            disabled={!selectedDomain}
            loading={isLoadingInstances}
            showSearch
            optionFilterProp='label'
            options={(domainInstances || []).map((inst) => ({
              label: `${inst.name}${inst.system_name ? ` (${inst.system_name})` : ''}`,
              value: inst.id,
            }))}
          />
        </Form.Item>
        <Form.Item label='Scopes' name='scopes'>
          <Select
            mode='multiple'
            placeholder='Select scopes'
            options={SCOPE_OPTIONS.map((scope) => ({
              label: scope,
              value: scope,
            }))}
          />
        </Form.Item>
        <Form.Item label='Active' name='active' valuePropName='checked'>
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
